import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Check,
  AlertTriangle,
  FileCheck2,
  FileX2,
  FileSignature,
  Receipt,
  ClipboardCheck,
  BellOff,
  Bell,
  ExternalLink,
  Settings,
  X,
  SlidersHorizontal,
  Minus,
  Send,
  Cloud,
  Bot,
  Copy,
} from "lucide-react";
import { todayISO, formatDateES } from "@/lib/utils-app";
import { isMinor, computeDocsStatus } from "@/lib/student-docs";
import JustificanteAusenciaDialog from "./documents/JustificanteAusenciaDialog";
import ContractsTab from "./ContractsTab";
import ExamenesDgtTab from "./ExamenesDgtTab";
import StudentSchedulingTab from "./StudentSchedulingTab";
import AltaDigitalLinkDialog from "./AltaDigitalLinkDialog";
import OneDriveVaultDialog from "./OneDriveVaultDialog";
import ExpedienteOneDrivePanel from "./ExpedienteOneDrivePanel";
import StudentFilesPanel from "./StudentFilesPanel";
import ReconciliationBanner from "./ReconciliationBanner";
import TasaRenewalBanner from "./TasaRenewalBanner";
import AltaDgtPanel from "./AltaDgtPanel";
import BitacoraDocumental from "./BitacoraDocumental";
import DgtDocumentsDialog from "./DgtDocumentsDialog";
import ReferralSection from "./ReferralSection";
import KaiStudentTab from "./KaiStudentTab";
import AnaStudentTab from "./AnaStudentTab";
import ExamRequestLogisticsDialog from "./ExamRequestLogisticsDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KAI_DEFAULT_TEMPLATE =
  "Hola {{nombre}}, soy Kai 🤖 de Natur Autoescuela. Necesitamos actualizar tu expediente. Completa este breve cuestionario: {{url}}";

function normalizePhoneES(raw) {
  const digits = String(raw || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits.length === 9) return "34" + digits;
  return digits;
}

function renderTpl(tpl, ctx) {
  return (tpl || "")
    .replace(/\{\{\s*nombre\s*\}\}/gi, ctx.nombre || "")
    .replace(/\{\{\s*url\s*\}\}/gi, ctx.url || "");
}

const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "horarios", label: "Horarios" },
  { key: "docs", label: "Cloud" },
  { key: "kai", label: "🤖 KAI" },
  { key: "ana", label: "👩‍💼 ANA" },
  { key: "examenes_dgt", label: "Exámenes DGT" },
  { key: "invoice", label: "Factura" },
  { key: "contratos", label: "Contratos" },
];

const CONTACT_STATUS_OPTIONS = [
  { key: "activo",             label: "Activo",              chip: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  { key: "pendiente_decision", label: "Pendiente decisión",  chip: "border-amber-200 bg-amber-50 text-amber-800" },
  { key: "no_interesado",      label: "No interesado",       chip: "border-neutral-300 bg-neutral-100 text-neutral-700" },
  { key: "contacto_pausado",   label: "Contacto pausado",    chip: "border-blue-200 bg-blue-50 text-blue-800" },
  { key: "no_contactar",       label: "No contactar",        chip: "border-red-300 bg-red-50 text-red-800" },
  { key: "baja_voluntaria",    label: "Baja voluntaria",     chip: "border-neutral-400 bg-neutral-200 text-neutral-800" },
];

function ContactStatusPanel({ student, onUpdated }) {
  const current = student.contact_status || "activo";
  const currentMeta = CONTACT_STATUS_OPTIONS.find((o) => o.key === current) || CONTACT_STATUS_OPTIONS[0];
  const paused = !!student.pause_automations;
  const [busy, setBusy] = useState(false);
  const [showPanel, setShowPanel] = useState(false);

  const changeStatus = async (newStatus) => {
    if (newStatus === current) { setShowPanel(false); return; }
    setBusy(true);
    try {
      await api.patch(`/students/${student.id}/contact-status`, { contact_status: newStatus, note: "" });
      toast.success(`Estado de contacto → ${CONTACT_STATUS_OPTIONS.find((o) => o.key === newStatus)?.label || newStatus}`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      setShowPanel(false);
    }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      await api.patch(`/students/${student.id}/pause-automations`, { pause_automations: !paused, note: "" });
      toast.success(paused ? "Automatizaciones reactivadas" : "Automatizaciones pausadas");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 mb-3"
      data-testid="contact-status-panel"
    >
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
          Estado de contacto
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => setShowPanel((v) => !v)}
          data-testid="contact-status-current"
          className={"inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold " + currentMeta.chip + " hover:brightness-95 disabled:opacity-50"}
          title="Click para cambiar el estado"
        >
          {currentMeta.label}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={togglePause}
          data-testid="pause-automations-toggle"
          className={
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ml-auto transition-colors " +
            (paused
              ? "border-orange-300 bg-orange-100 text-orange-800 hover:bg-orange-200"
              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100")
          }
          title="Pausa temporalmente los recordatorios automáticos"
        >
          {paused ? "🔕 Automatizaciones pausadas" : "🔔 Automatizaciones activas"}
        </button>
      </div>
      {showPanel && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2" data-testid="contact-status-menu">
          {CONTACT_STATUS_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              disabled={busy}
              onClick={() => changeStatus(o.key)}
              data-testid={`contact-status-option-${o.key}`}
              className={
                "inline-flex items-center justify-center rounded-lg border px-2 py-1.5 text-[11px] font-bold transition-colors " +
                (o.key === current
                  ? o.chip + " ring-2 ring-neutral-400"
                  : o.chip + " opacity-70 hover:opacity-100")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {(current === "no_contactar" || current === "baja_voluntaria" || paused) && (
        <div className="mt-2 text-[11px] text-neutral-600 flex items-start gap-1.5">
          <span className="text-red-600 font-bold">⚠</span>
          <span>
            KAI <b>no enviará</b> recordatorios automáticos ni escalará incidencias para este alumno. La ficha sigue visible y la intervención manual sigue permitida.
          </span>
        </div>
      )}
    </div>
  );
}

function DocsAlertBanner({ student, docsStatus, onUpdated }) {
  const [reasons, setReasons] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newReason, setNewReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isHidden = !!student.docs_alert_hidden;
  const hasPending = !docsStatus.complete;

  const loadReasons = useCallback(async () => {
    try {
      const { data } = await api.get("/doc-alert-reasons");
      setReasons(data);
    } catch (err) {
      console.warn("Could not load reasons:", err);
    }
  }, []);

  useEffect(() => {
    if (showMenu) loadReasons();
  }, [showMenu, loadReasons]);

  const hideAlert = async (reason) => {
    setBusy(true);
    try {
      await api.post(`/students/${student.id}/hide-docs-alert`, { reason });
      toast.success("Alerta ocultada");
      setShowMenu(false);
      setAddingNew(false);
      setNewReason("");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const showAlertAgain = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${student.id}/show-docs-alert`);
      toast.success("Alerta reactivada");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createAndHide = async (e) => {
    e.preventDefault();
    const name = newReason.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/doc-alert-reasons", { name });
      await hideAlert(name);
    } catch (err) {
      // If reason already exists, fall back to using its name
      if (err?.response?.status === 400) {
        await hideAlert(name);
        return;
      }
      toast.error(formatApiError(err?.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!hasPending && !isHidden) return null;

  if (isHidden) {
    return (
      <div
        data-testid="docs-alert-hidden"
        className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-700 flex items-start gap-2 flex-wrap"
      >
        <BellOff className="h-4 w-4 mt-0.5 shrink-0 text-neutral-400" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">Alerta de documentación ocultada</div>
          <div className="text-xs text-neutral-500">
            Motivo: <span className="font-medium text-neutral-700">{student.docs_alert_reason || "—"}</span>
            {student.docs_alert_hidden_at && (
              <> · {formatDateES(student.docs_alert_hidden_at.slice(0, 10))}</>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={showAlertAgain}
          disabled={busy}
          data-testid="docs-alert-show-btn"
          className="btn-ghost border border-neutral-200 !text-xs !h-8"
        >
          <Bell className="h-3.5 w-3.5" />
          Reactivar
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="docs-alert-banner"
      className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 space-y-2"
    >
      <div className="flex items-start gap-2 flex-wrap">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold">Alumno con documentación pendiente</div>
          <div className="text-xs opacity-90 mt-0.5">
            Faltan: <span className="font-medium">{docsStatus.missing.join(" · ")}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowMenu((v) => !v)}
          disabled={busy}
          data-testid="docs-alert-hide-btn"
          className="btn-ghost border border-amber-300 text-amber-800 hover:bg-amber-100 !text-xs !h-8"
        >
          <BellOff className="h-3.5 w-3.5" />
          Ocultar alerta
        </button>
      </div>

      {showMenu && (
        <div
          data-testid="docs-alert-menu"
          className="bg-white border border-amber-200 rounded-lg p-2 space-y-1"
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 px-2 py-1">
            Selecciona un motivo
          </div>
          {reasons.length === 0 && (
            <div className="text-xs text-neutral-500 px-2 py-1">Cargando motivos…</div>
          )}
          {reasons.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => hideAlert(r.name)}
              disabled={busy}
              data-testid={`docs-alert-reason-${r.id}`}
              className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-amber-50 text-neutral-800 transition-colors"
            >
              {r.name}
            </button>
          ))}
          <div className="border-t border-neutral-100 pt-1 mt-1">
            {!addingNew ? (
              <button
                type="button"
                onClick={() => setAddingNew(true)}
                data-testid="docs-alert-add-reason"
                className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-neutral-50 text-orange-600 font-semibold inline-flex items-center gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Añadir nuevo motivo
              </button>
            ) : (
              <form onSubmit={createAndHide} className="flex gap-1 px-1 py-1">
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="Nuevo motivo"
                  autoFocus
                  required
                  data-testid="docs-alert-new-reason-input"
                  className="flex-1 rounded-md border border-neutral-300 bg-white px-2 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  disabled={busy || !newReason.trim()}
                  data-testid="docs-alert-new-reason-submit"
                  className="h-8 px-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50"
                >
                  Guardar
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, highlight }) {
  return (
    <div
      className={
        "rounded-xl border px-3 py-3 " +
        (highlight
          ? "border-orange-200 bg-orange-50"
          : accent
          ? "border-neutral-200 bg-neutral-50"
          : "border-neutral-200 bg-white")
      }
    >
      <div className="text-[11px] uppercase tracking-wide text-neutral-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function ManualAdjustmentCard({ student, remaining, onApplied }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const numDelta = parseInt(delta, 10);
  const isValid = !isNaN(numDelta) && numDelta !== 0 && reason.trim().length > 0;

  const apply = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      // The endpoint is PIN-protected (class_adjustment.create). The axios
      // interceptor will surface the PIN dialog automatically if the sudo
      // session is not yet active.
      await api.post(`/students/${student.id}/class-adjustments`, {
        delta: numDelta,
        reason: reason.trim(),
      });
      const sign = numDelta > 0 ? "+" : "";
      toast.success(
        `Ajuste aplicado (${sign}${numDelta} clase${Math.abs(numDelta) === 1 ? "" : "s"})`,
        { duration: 4000 }
      );
      setDelta("");
      setReason("");
      onApplied?.();
    } catch (err) {
      // The interceptor returns without retrying if the user cancels the PIN dialog.
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const preview = !isNaN(numDelta) && numDelta !== 0 ? remaining + numDelta : remaining;

  return (
    <div className="card-surface p-4 space-y-3 border-l-4 border-l-orange-400" data-testid="manual-adjustment-card">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-orange-500" />
        <h3 className="font-bold">Ajuste manual de clases</h3>
      </div>
      <p className="text-xs text-neutral-500">
        Añade o resta clases del saldo del alumno para resolver incidencias, sin modificar facturas ni bonos existentes.
        Cada ajuste requiere <span className="font-semibold text-neutral-700">PIN maestro</span> y queda registrado en la auditoría.
      </p>
      <form onSubmit={apply} className="space-y-3">
        <div className="grid grid-cols-[auto_1fr] gap-2 items-stretch">
          <div className="flex items-stretch rounded-lg overflow-hidden border border-neutral-300 bg-white">
            <button
              type="button"
              onClick={() => {
                const n = parseInt(delta || "0", 10);
                setDelta(String((isNaN(n) ? 0 : n) - 1));
              }}
              data-testid="adjust-minus"
              className="px-2 hover:bg-neutral-100 text-neutral-600"
              aria-label="Restar"
            >
              <Minus className="h-4 w-4" />
            </button>
            <input
              type="number"
              inputMode="numeric"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="±0"
              data-testid="adjust-delta-input"
              className="w-24 h-10 text-center text-base font-bold tabular-nums text-neutral-900 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => {
                const n = parseInt(delta || "0", 10);
                setDelta(String((isNaN(n) ? 0 : n) + 1));
              }}
              data-testid="adjust-plus"
              className="px-2 hover:bg-neutral-100 text-neutral-600"
              aria-label="Sumar"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (obligatorio) — ej: clase regalo, corrección de bono, etc."
            data-testid="adjust-reason-input"
            className="rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="text-neutral-500">
            Restantes actuales: <span className="font-mono font-bold text-neutral-900">{remaining}</span>
          </div>
          {!isNaN(numDelta) && numDelta !== 0 && (
            <div
              data-testid="adjust-preview"
              className={
                "font-semibold tabular-nums " +
                (preview < 0 ? "text-red-600" : "text-emerald-700")
              }
            >
              → Nuevo saldo: {preview}
              {preview < 0 && " ⚠️ Negativo"}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!isValid || saving}
            data-testid="adjust-apply"
            className="btn-primary"
          >
            <Check className="h-4 w-4" />
            {saving ? "Aplicando…" : "Aplicar ajuste"}
          </button>
        </div>
      </form>
    </div>
  );
}


function PurchaseHistoryRow({ p }) {
  const isAdjust = p.source === "adjustment";
  const isInvoice = p.source === "invoice";
  const positive = p.classes_bought > 0;
  return (
    <div className="flex items-start justify-between px-4 py-3 text-sm gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={
              "font-semibold tabular-nums " +
              (positive ? "text-neutral-900" : "text-red-700")
            }
          >
            {positive ? "+" : ""}
            {p.classes_bought} clase{Math.abs(p.classes_bought) === 1 ? "" : "s"}
          </span>
          {isAdjust && (
            <span className="text-[10px] font-semibold rounded-full bg-orange-100 text-orange-800 px-2 py-0.5">
              AJUSTE
            </span>
          )}
          {isInvoice && (
            <span className="text-[10px] font-semibold rounded-full bg-blue-100 text-blue-800 px-2 py-0.5">
              FACTURA {p.invoice_number || ""}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">
          {formatDateES(p.date)}
          {p.reason && <> · <span className="italic">{p.reason}</span></>}
          {p.actor_email && <> · <span>{p.actor_email}</span></>}
        </div>
      </div>
    </div>
  );
}

function ResumenSection({ summary, student, onReload }) {
  const [invoicesCount, setInvoicesCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/students/${student.id}/invoices`);
        if (!cancelled) setInvoicesCount(data.length);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [student.id]);

  const adjustments = summary.adjustments || 0;
  const hasInvoices = invoicesCount === null ? null : invoicesCount > 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <Stat label="Contratadas" value={summary.contracted} accent />
        <Stat label="Realizadas" value={summary.done} />
        <Stat label="Programadas" value={summary.scheduled} />
        <Stat label="No asistidas" value={summary.missed} />
        <Stat
          label="Ajustes"
          value={`${adjustments > 0 ? "+" : ""}${adjustments}`}
        />
        <Stat label="Restantes" value={summary.remaining} highlight />
      </div>

      <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 px-3 py-2 text-[11px] text-neutral-600" data-testid="balance-formula">
        <span className="font-semibold text-neutral-800">Fórmula:</span>{" "}
        Restantes = Contratadas ({summary.contracted}) − Realizadas ({summary.done}) − Programadas ({summary.scheduled}) − No asistidas ({summary.missed}) {adjustments >= 0 ? "+" : "−"} Ajustes ({Math.abs(adjustments)}) = <span className="font-bold text-neutral-900">{summary.remaining}</span>
        <div className="mt-1 text-neutral-500">
          El total contratado es <span className="font-semibold text-neutral-700">histórico</span> — suma de todas las facturas. Los ajustes manuales no lo alteran, solo afectan al saldo disponible.
        </div>
      </div>

      {hasInvoices === false && (
        <div
          data-testid="invoice-pending-notice"
          className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="font-semibold">Este alumno aún no tiene facturas registradas</span>
        </div>
      )}

      <ManualAdjustmentCard
        student={student}
        remaining={summary.remaining}
        onApplied={onReload}
      />

      {student?.notes && (
        <div className="card-surface p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 mb-1">
            Observaciones
          </div>
          <p className="text-sm text-neutral-700 whitespace-pre-wrap">{student.notes}</p>
        </div>
      )}
      <div>
        <div className="text-sm font-semibold mb-2 text-neutral-700">Historial de compras y ajustes</div>
        {summary.purchases.length === 0 ? (
          <div className="text-sm text-neutral-500">Aún no hay movimientos.</div>
        ) : (
          <div className="card-surface divide-y divide-neutral-100" data-testid="purchase-history">
            {summary.purchases.map((p) => (
              <PurchaseHistoryRow key={p.id} p={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// V1.33 · Paleta de categorías (Tailwind tokens → clases badge)
const CATEGORY_STYLES = {
  gray:    "bg-neutral-100 text-neutral-800 border-neutral-200",
  indigo:  "bg-indigo-100 text-indigo-800 border-indigo-200",
  orange:  "bg-orange-100 text-orange-800 border-orange-200",
  red:     "bg-red-100 text-red-800 border-red-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  neutral: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

function DocsSection({ student, onReload }) {
  const minor = isMinor(student.birth_date);
  const docs = student.documents || [];
  const visible = useMemo(() => {
    const filtered = docs.filter((d) => !d.only_minor || minor);
    // Orden: order asc, y por nombre como desempate
    return [...filtered].sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.name || "").localeCompare(b.name || ""));
  }, [docs, minor]);

  const [templates, setTemplates] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null);
  const [expanded, setExpanded] = useState(null); // doc.id expandido
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/document-templates");
        setTemplates(data || []);
      } catch (err) { console.warn(err); }
    })();
  }, []);

  const loadNotes = useCallback(async () => {
    if (!student?.id) return;
    setNotesLoading(true);
    try {
      const { data } = await api.get(`/students/${student.id}/document-notes`);
      setNotes(data || []);
    } catch (err) {
      console.warn(err);
    } finally { setNotesLoading(false); }
  }, [student?.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const availableTemplates = useMemo(() => {
    const usedKeys = new Set(docs.map((d) => d.key));
    return templates.filter((t) => !usedKeys.has(t.key) && (!t.only_minor || minor));
  }, [templates, docs, minor]);

  const currentStatus = (d) => d.status || (d.delivered ? "validado" : "pendiente");

  const transition = async (doc, status) => {
    setBusyDoc(doc.id);
    try {
      await api.post(`/students/${student.id}/documents/${doc.id}/status`, { status });
      const labels = { pendiente: "Pendiente", solicitado: "Solicitado", recibido: "Recibido", validado: "Validado", rechazado: "Rechazado" };
      toast.success(`Documento → ${labels[status] || status}`);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusyDoc(null); }
  };

  const remindDoc = async (doc) => {
    setBusyDoc(doc.id);
    try {
      await api.post(`/students/${student.id}/documents/${doc.id}/remind`, { channel: "whatsapp" });
      const phone = String(student.phone || "").replace(/[^\d]/g, "");
      const norm = phone.length === 9 ? "34" + phone : phone;
      const first = (student.name || "").split(" ")[0] || "";
      const msg = `Hola ${first}, te recordamos que necesitamos el documento: ${doc.name}. Gracias — Natur Autoescuela.`;
      const url = norm ? `https://wa.me/${norm}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Recordatorio registrado");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusyDoc(null); }
  };

  const updateField = async (doc, field, value) => {
    try {
      await api.patch(`/students/${student.id}/documents/${doc.id}`, { [field]: value });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const addFromTemplate = async (template) => {
    try {
      await api.post(`/students/${student.id}/documents`, { template_key: template.key });
      toast.success(`${template.name} añadido al expediente`);
      setShowPicker(false);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    const note = newNote.trim();
    if (!note) return;
    setAddingNote(true);
    try {
      await api.post(`/students/${student.id}/document-notes`, { note });
      setNewNote("");
      toast.success("Nota añadida");
      loadNotes();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setAddingNote(false); }
  };

  return (
    <div className="space-y-3">
      {visible.map((d) => {
        const status = currentStatus(d);
        const statusStyles = {
          pendiente:  "bg-neutral-100 text-neutral-700 border-neutral-200",
          solicitado: "bg-amber-100 text-amber-800 border-amber-200",
          recibido:   "bg-blue-100 text-blue-800 border-blue-200",
          validado:   "bg-emerald-100 text-emerald-800 border-emerald-200",
          rechazado:  "bg-red-100 text-red-800 border-red-200",
        };
        const statusLabels = { pendiente: "Pendiente", solicitado: "Solicitado", recibido: "Recibido", validado: "Validado", rechazado: "Rechazado" };
        const catBadge = CATEGORY_STYLES[d.color || "neutral"] || CATEGORY_STYLES.neutral;
        const isValidated = status === "validado";
        const isExpanded = expanded === d.id;
        const remindersCount = (d.reminders || []).length;
        return (
          <div key={d.id} data-testid={`doc-row-${d.id}`}
               className={"card-surface px-3 py-2 " + (isValidated ? "bg-green-50/40" : "")}>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => transition(d, isValidated ? "pendiente" : "validado")}
                data-testid={`doc-toggle-${d.id}`}
                className={"h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors " +
                  (isValidated ? "bg-green-600 border-green-600 text-white" : "border-neutral-300 hover:border-orange-500")}
                title={isValidated ? "Marcar como pendiente" : "Marcar como validado"}>
                {isValidated && <Check className="h-3.5 w-3.5" />}
              </button>
              <button type="button" onClick={() => setExpanded(isExpanded ? null : d.id)}
                data-testid={`doc-expand-${d.id}`}
                className="flex-1 min-w-0 flex items-center gap-2 flex-wrap text-left">
                <span className="font-semibold text-sm truncate">{d.name}</span>
                {d.category && d.category !== "OTROS" && (
                  <span className={"inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider " + catBadge}
                    data-testid={`doc-category-${d.id}`}>{d.category}</span>
                )}
                <span data-testid={`doc-status-${d.id}`}
                  className={"inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-bold " + (statusStyles[status] || statusStyles.pendiente)}>
                  {statusLabels[status] || status}
                </span>
                {d.mandatory && (
                  <span className="text-[10px] font-bold text-orange-700" title="Obligatorio">•</span>
                )}
                {remindersCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-neutral-900 text-white text-[10px] font-bold px-1.5 py-0.5"
                    title={`${remindersCount} recordatorio(s)`} data-testid={`doc-reminders-${d.id}`}>
                    <BellOff className="h-2.5 w-2.5" /> {remindersCount}
                  </span>
                )}
              </button>
              <input type="date" value={d.delivered_at || ""}
                onChange={(e) => updateField(d, "delivered_at", e.target.value)}
                data-testid={`doc-date-${d.id}`}
                title="Fecha del documento/recepción — siempre editable"
                className="rounded-md border border-neutral-300 bg-white px-1.5 h-7 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>

            {/* Acciones rápidas — sólo visibles cuando no está validado */}
            {!isValidated && (
              <div className="flex flex-wrap gap-1 mt-1.5 ml-9" data-testid={`doc-actions-${d.id}`}>
                <button type="button" disabled={busyDoc === d.id} onClick={() => remindDoc(d)}
                  data-testid={`doc-remind-${d.id}`}
                  className="inline-flex items-center gap-1 h-6 px-1.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-[10px] font-semibold disabled:opacity-40">
                  <Send className="h-2.5 w-2.5" /> Recordar
                </button>
                {status !== "recibido" && (
                  <button type="button" disabled={busyDoc === d.id} onClick={() => transition(d, "recibido")}
                    data-testid={`doc-recibido-${d.id}`}
                    className="inline-flex items-center gap-1 h-6 px-1.5 rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 text-[10px] font-semibold disabled:opacity-40">
                    <FileCheck2 className="h-2.5 w-2.5" /> Recibido
                  </button>
                )}
                <button type="button" disabled={busyDoc === d.id} onClick={() => transition(d, "validado")}
                  data-testid={`doc-validado-${d.id}`}
                  className="inline-flex items-center gap-1 h-6 px-1.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-[10px] font-semibold disabled:opacity-40">
                  <Check className="h-2.5 w-2.5" /> Validar
                </button>
                {status !== "rechazado" && (
                  <button type="button" disabled={busyDoc === d.id} onClick={() => transition(d, "rechazado")}
                    data-testid={`doc-rechazado-${d.id}`}
                    className="inline-flex items-center gap-1 h-6 px-1.5 rounded border border-red-300 bg-red-50 hover:bg-red-100 text-red-800 text-[10px] font-semibold disabled:opacity-40">
                    <FileX2 className="h-2.5 w-2.5" /> Rechazar
                  </button>
                )}
              </div>
            )}

            {/* Panel expandible con información adicional */}
            {isExpanded && (
              <div className="mt-2 ml-9 pt-2 border-t border-neutral-100 text-[11px] text-neutral-600 space-y-1" data-testid={`doc-detail-${d.id}`}>
                {d.sensitivity === "sanitario" && (
                  <div><span className="font-semibold text-red-700">SANITARIO</span> · política de conservación especial</div>
                )}
                {d.status_updated_at && (
                  <div>Último cambio de estado: {formatDateES(d.status_updated_at.slice(0,10))}</div>
                )}
                {(d.status_history || []).length > 0 && (
                  <div>
                    <div className="font-semibold text-neutral-700 mb-0.5">Historial:</div>
                    <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                      {d.status_history.slice(-5).reverse().map((h, i) => (
                        <li key={i} className="font-mono">
                          {h.at?.slice(0,10)} · <b>{h.status}</b>{h.actor ? ` · ${h.actor}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(d.reminders || []).length > 0 && (
                  <div>Recordatorios enviados: <b>{d.reminders.length}</b></div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Añadir documento (Picker desde Matriz Documental) */}
      {!showPicker ? (
        <button type="button" onClick={() => setShowPicker(true)} data-testid="doc-add-open"
          className="btn-ghost border border-dashed border-neutral-300 hover:border-orange-400 hover:text-orange-600 w-full justify-center !h-10"
          disabled={availableTemplates.length === 0}
          title={availableTemplates.length === 0 ? "Todos los documentos de la Matriz ya están en el expediente" : ""}>
          <Plus className="h-4 w-4" /> Añadir documento{availableTemplates.length > 0 && ` (${availableTemplates.length} disponibles)`}
        </button>
      ) : (
        <div className="card-surface p-3 space-y-2" data-testid="doc-picker">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Plus className="h-4 w-4 text-orange-500" /> Añadir desde Matriz Documental
            </div>
            <button type="button" onClick={() => setShowPicker(false)} className="text-neutral-500 hover:text-neutral-900" data-testid="doc-picker-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          {availableTemplates.length === 0 ? (
            <div className="text-xs text-neutral-500 italic">Todos los documentos ya están asignados.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {availableTemplates.map((t) => {
                const badge = CATEGORY_STYLES[t.color || "neutral"] || CATEGORY_STYLES.neutral;
                return (
                  <button key={t.key} type="button" onClick={() => addFromTemplate(t)}
                    data-testid={`doc-picker-item-${t.key}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border border-neutral-200 bg-white hover:bg-orange-50 hover:border-orange-300 text-left transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{t.name}</span>
                      {t.mandatory && <span className="text-[10px] font-bold text-orange-700" title="Obligatorio">•</span>}
                    </div>
                    <span className={"inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider " + badge}>
                      {t.category}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* V1.33-B · Bitácora Documental (reemplaza notas por documento) */}
      <BitacoraDocumental studentId={student.id} onEmpty={loadNotes} />
    </div>
  );
}


function ExamRequestsSection({ student, onReload }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ presented_date: todayISO(), tasas_number: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [detailReq, setDetailReq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${student.id}/exam-requests`);
      setRequests(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.tasas_number.trim() || !form.presented_date) {
      toast.error("Fecha y nº de tasas son obligatorios");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/students/${student.id}/exam-requests`, form);
      toast.success("Solicitud creada");
      setForm({ presented_date: todayISO(), tasas_number: "", notes: "" });
      load();
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const patchReq = async (id, update) => {
    try {
      await api.patch(`/exam-requests/${id}`, update);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const removeReq = async (id) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try {
      await api.delete(`/exam-requests/${id}`);
      toast.success("Solicitud eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="card-surface p-4 space-y-3">
        <div className="text-sm font-semibold">Nueva solicitud</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Fecha de presentación</label>
            <input
              data-testid="exam-req-date"
              type="date"
              value={form.presented_date}
              onChange={(e) => setForm({ ...form, presented_date: e.target.value })}
              required
              className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Nº de tasas</label>
            <input
              data-testid="exam-req-tasas"
              type="text"
              value={form.tasas_number}
              onChange={(e) => setForm({ ...form, tasas_number: e.target.value })}
              required
              className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        <input
          type="text"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Observaciones (opcional)"
          className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button data-testid="exam-req-create" disabled={creating} className="btn-primary">
          <Plus className="h-4 w-4" />
          Crear solicitud
        </button>
      </form>

      {loading ? (
        <div className="text-center text-sm text-neutral-500 py-6">Cargando…</div>
      ) : requests.length === 0 ? (
        <div className="text-center text-sm text-neutral-500 py-6">Aún no hay solicitudes.</div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div
              key={r.id}
              data-testid={`exam-req-${r.id}`}
              role="button"
              tabIndex={0}
              onClick={() => setDetailReq(r)}
              onKeyDown={(e) => { if (e.key === "Enter") setDetailReq(r); }}
              className={
                "card-surface p-4 cursor-pointer hover:border-orange-300 transition-colors " +
                (r.status === "active" ? "border-l-4 border-l-blue-500" : "opacity-80")
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">
                    Tasas <span className="font-mono">{r.tasas_number}</span>
                  </div>
                  <div className="text-xs text-neutral-500">
                    Presentada: {formatDateES(r.presented_date)} · Intentos: {r.attempts_used}/2
                  </div>
                </div>
                <span
                  className={
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold " +
                    (r.status === "active" ? "bg-blue-100 text-blue-800" : "bg-neutral-200 text-neutral-700")
                  }
                >
                  {r.status === "active" ? "Activa" : "Finalizada"}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-sm">
                <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!r.documentation_complete}
                    onChange={(e) => patchReq(r.id, { documentation_complete: e.target.checked })}
                    className="h-4 w-4 accent-orange-500"
                  />
                  Documentación completa
                </label>
                <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!r.fees_paid}
                    onChange={(e) => patchReq(r.id, { fees_paid: e.target.checked })}
                    className="h-4 w-4 accent-orange-500"
                  />
                  Tasas pagadas
                </label>
                <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!r.presented_traffic}
                    onChange={(e) => patchReq(r.id, { presented_traffic: e.target.checked })}
                    className="h-4 w-4 accent-orange-500"
                  />
                  Presentada en tráfico
                </label>
              </div>
              <div className="flex gap-2 mt-3">
                {r.status === "active" ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); patchReq(r.id, { status: "finished" }); }}
                    className="btn-ghost border border-neutral-200 text-xs"
                  >
                    Marcar finalizada
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); patchReq(r.id, { status: "active" }); }}
                    className="btn-ghost border border-neutral-200 text-xs"
                  >
                    Reabrir
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); removeReq(r.id); }}
                  className="btn-ghost text-red-600 hover:bg-red-50 !px-2 ml-auto"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ExamRequestLogisticsDialog
        request={detailReq}
        open={!!detailReq}
        onClose={() => setDetailReq(null)}
      />
    </div>
  );
}

function A3FacturaButton() {
  const [url, setUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/a3factura_url");
        setUrl(data.value || "");
      } catch (err) {
        console.warn("Could not load a3factura url", err);
      }
    })();
  }, []);

  const open = () => {
    if (!url) {
      toast.error("Configura primero la URL de a3factura");
      setEditing(true);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const save = async (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      toast.error("La URL debe empezar por http:// o https://");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/settings/a3factura_url", { value: v });
      setUrl(v);
      setEditing(false);
      toast.success("URL guardada");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={open}
        data-testid="a3factura-open"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
      >
        <ExternalLink className="h-4 w-4" />
        Abrir en a3factura
      </button>
      <button
        type="button"
        onClick={() => {
          setDraft(url);
          setEditing((v) => !v);
        }}
        data-testid="a3factura-config-toggle"
        title="Configurar URL"
        className="h-9 w-9 rounded-lg border border-neutral-200 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 inline-flex items-center justify-center"
      >
        <Settings className="h-4 w-4" />
      </button>
      {editing && (
        <form
          onSubmit={save}
          className="w-full flex flex-wrap gap-2 items-center bg-neutral-50 border border-neutral-200 rounded-lg p-2"
        >
          <input
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://login-integraciones.wolterskluwer.es/"
            required
            autoFocus
            data-testid="a3factura-url-input"
            className="flex-1 min-w-[220px] rounded-md border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="a3factura-url-save"
            className="h-9 px-3 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Guardar
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="h-9 w-9 rounded-md text-neutral-500 hover:bg-neutral-100 inline-flex items-center justify-center"
            title="Cancelar"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      )}
    </div>
  );
}

function InvoiceForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    number: initial?.number || "",
    date: initial?.date || todayISO(),
    emitted: !!initial?.emitted,
    notes: initial?.notes || "",
    classes_included: Number(initial?.classes_included || 0),
  });

  const submit = (e) => {
    e.preventDefault();
    if (form.emitted && !form.number.trim()) {
      toast.error("Para marcar como emitida es necesario un nº de factura");
      return;
    }
    onSave({ ...form, classes_included: Math.max(0, parseInt(form.classes_included || "0", 10)) });
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/50 p-3" data-testid="invoice-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-neutral-600 mb-1 block">
            Nº de factura {form.emitted && <span className="text-red-500">*</span>}
          </label>
          <input
            data-testid="invoice-number"
            type="text"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            placeholder="Ej. F-2026-001"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-neutral-600 mb-1 block">Fecha de emisión</label>
          <input
            data-testid="invoice-date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-neutral-600 mb-1 block">Nº de clases contratadas</label>
        <input
          data-testid="invoice-classes-included"
          type="number"
          min="0"
          value={form.classes_included}
          onChange={(e) => setForm({ ...form, classes_included: e.target.value })}
          className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <p className="text-[11px] text-neutral-500 mt-1">
          Al guardar, estas clases se suman al saldo del alumno. Las facturas se acumulan históricamente — cada una añade sus clases al total contratado.
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.emitted}
          onChange={(e) => setForm({ ...form, emitted: e.target.checked })}
          className="h-4 w-4 accent-orange-500"
          data-testid="invoice-emitted"
        />
        Factura emitida
      </label>
      <textarea
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        rows={2}
        placeholder="Observaciones"
        data-testid="invoice-notes"
        className="w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-secondary" data-testid="invoice-form-cancel">
            <X className="h-4 w-4" /> Cancelar
          </button>
        )}
        <button type="submit" disabled={saving} className="btn-primary" data-testid="invoice-save">
          <Check className="h-4 w-4" />
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </form>
  );
}


function InvoiceSection({ student, onReload }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // V1.82 · «FALTA FACTURA»: deja registrada la factura de venta pendiente
  // de emitir sin tener que escribir nada (el nº llega luego de a3factura).
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingClasses, setPendingClasses] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${student.id}/invoices`);
      setInvoices(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editingId) {
        const previous = invoices.find((i) => i.id === editingId);
        const delta = Number(payload.classes_included || 0) - Number(previous?.classes_included || 0);
        await api.patch(`/students/${student.id}/invoices/${editingId}`, payload);
        if (delta !== 0) {
          toast.success(
            `Factura actualizada · ${delta > 0 ? "+" : ""}${delta} clase${Math.abs(delta) === 1 ? "" : "s"} en el saldo`,
            { duration: 4000 }
          );
        } else {
          toast.success("Factura actualizada");
        }
      } else {
        await api.post(`/students/${student.id}/invoices`, payload);
        const added = Number(payload.classes_included || 0);
        if (added > 0) {
          toast.success(
            `Factura creada · +${added} clase${added === 1 ? "" : "s"} añadida${added === 1 ? "" : "s"} al total contratado`,
            { duration: 4000 }
          );
        } else {
          toast.success("Factura creada");
        }
      }
      setCreating(false);
      setEditingId(null);
      await load();
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markMissing = async () => {
    setSaving(true);
    try {
      await api.post(`/students/${student.id}/invoices`, {
        number: "",
        date: new Date().toISOString().slice(0, 10),
        emitted: false,
        classes_included: Number(pendingClasses || 0),
        notes: "FALTA FACTURA",
      });
      toast.success("Marcado como factura pendiente · visible en Contabilidad · Ventas pendientes", { duration: 4000 });
      setPendingOpen(false);
      setPendingClasses("");
      await load();
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (inv) => {    if (!window.confirm(`¿Eliminar la factura ${inv.number || "(sin número)"}? El total contratado se reducirá en ${inv.classes_included} clase(s).`)) return;
    try {
      await api.delete(`/students/${student.id}/invoices/${inv.id}`);
      toast.success("Factura eliminada");
      await load();
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const totalClasses = invoices.reduce((s, i) => s + Number(i.classes_included || 0), 0);

  const editing = editingId ? invoices.find((i) => i.id === editingId) : null;

  return (
    <div className="card-surface p-4 space-y-3" data-testid="invoices-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Receipt className="h-5 w-5 text-orange-500" />
        <h3 className="font-bold">Facturas</h3>
        <span className="text-xs text-neutral-500">
          {invoices.length === 0 ? "sin facturas" : `${invoices.length} factura${invoices.length === 1 ? "" : "s"} · ${totalClasses} clase${totalClasses === 1 ? "" : "s"} en total`}
        </span>
        {!creating && !editing && (
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setPendingOpen((v) => !v)}
              data-testid="invoice-missing-button"
              title="Marcar que falta emitir la factura de este alumno"
              className="btn-secondary !h-9 !text-xs !text-amber-800 !border-amber-300 hover:!bg-amber-50"
            >
              <AlertTriangle className="h-3.5 w-3.5" /> FALTA FACTURA
            </button>
            <button
              onClick={() => setCreating(true)}
              data-testid="invoice-new-button"
              className="btn-primary !h-9 !text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Nueva factura
            </button>
          </div>
        )}
      </div>
      {pendingOpen && (
        <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="invoice-missing-form">
          <div className="text-xs font-semibold text-amber-900">
            Marcar factura pendiente — queda listada en Contabilidad · Ventas pendientes
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 block mb-0.5">
                Nº de clases (opcional)
              </label>
              <input
                type="number" min="0" value={pendingClasses}
                onChange={(e) => setPendingClasses(e.target.value)}
                data-testid="invoice-missing-classes"
                className="w-32 rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <button
              onClick={markMissing}
              disabled={saving}
              data-testid="invoice-missing-save"
              className="h-9 px-3 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
            >
              Marcar pendiente
            </button>
            <button
              onClick={() => setPendingOpen(false)}
              className="h-9 px-3 rounded-lg text-xs font-semibold text-neutral-600 hover:bg-neutral-100"
            >
              Cancelar
            </button>
          </div>
          <p className="text-[11px] text-neutral-600">
            Si dejas las clases en blanco no se toca el saldo del alumno: solo queda el aviso de que falta emitir la factura.
          </p>
        </div>
      )}
      <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-[11px] text-neutral-600 leading-snug">
        Las facturas se generan en <span className="font-semibold text-neutral-800">Wolters Kluwer (a3factura)</span>.
        Este módulo solo registra la referencia interna y acumula las clases contratadas al saldo del alumno.
      </div>
      <A3FacturaButton />

      {(creating || editing) && (
        <InvoiceForm
          initial={editing || null}
          onSave={save}
          onCancel={() => { setCreating(false); setEditingId(null); }}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="text-sm text-neutral-500 py-3">Cargando…</div>
      ) : invoices.length === 0 && !creating ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-6 text-center text-sm text-neutral-500">
          Aún no hay facturas registradas. Pulsa &quot;Nueva factura&quot; para añadir la primera.
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoice-list">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              data-testid={`invoice-row-${inv.id}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-neutral-200 rounded-xl px-3 py-2.5 bg-white"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-sm text-neutral-900">{inv.number || "—"}</span>
                  <span
                    className={
                      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                      (inv.emitted ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800")
                    }
                  >
                    {inv.emitted ? "Emitida" : "Pendiente"}
                  </span>
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {inv.date ? formatDateES(inv.date) : "sin fecha"}
                  {inv.notes && <> · <span className="italic">{inv.notes}</span></>}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">Clases</div>
                  <div className="font-mono font-bold text-lg text-neutral-900">{inv.classes_included || 0}</div>
                </div>
                <button
                  onClick={() => { setEditingId(inv.id); setCreating(false); }}
                  className="btn-ghost !h-8 !px-2"
                  data-testid={`invoice-edit-${inv.id}`}
                  title="Editar"
                >
                  <Settings className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/students/${student.id}/invoices/${inv.id}/duplicate`);
                      toast.success("Factura duplicada (borrador) · rellena el número y emítela.");
                      await load();
                      onReload?.();
                    } catch (err) {
                      toast.error(formatApiError(err?.response?.data?.detail));
                    }
                  }}
                  className="btn-ghost !h-8 !px-2 text-blue-600 hover:bg-blue-50"
                  data-testid={`invoice-duplicate-${inv.id}`}
                  title="Duplicar esta factura"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => remove(inv)}
                  className="btn-ghost !h-8 !px-2 text-red-600 hover:bg-red-50"
                  data-testid={`invoice-delete-${inv.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StudentSummaryDialog({ student, onClose, initialTab = "resumen" }) {
  // V1.91 · `initialTab` permite abrir directamente en Horarios al pinchar el
  // nombre del alumno desde el calendario.
  const [tab, setTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  // V1.25.4 · Integridad de datos: NUNCA inicializar con `student` (crea estado
  // rancio cuando el mismo componente se reutiliza para distintos alumnos).
  // fullStudent se hidrata SIEMPRE desde el backend con el student_id vigente.
  const [fullStudent, setFullStudent] = useState(null);
  const [showJustificante, setShowJustificante] = useState(false);
  const [showAltaLink, setShowAltaLink] = useState(false);
  const [showOnedrive, setShowOnedrive] = useState(false);
  const [showDgt, setShowDgt] = useState(false);

  const studentId = student?.id;
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/summary`);
      // V1.25.4 · Guardia de integridad: rechazar la respuesta si no coincide
      // con el alumno solicitado (ej. carrera de peticiones al cambiar de ficha).
      if (data?.student?.id && data.student.id !== studentId) {
        console.error(
          "[data-integrity] Respuesta /summary con id distinto",
          { requested: studentId, received: data.student.id },
        );
        toast.error("Se ha detectado un cruce de datos. Recargando…");
        return;
      }
      setSummary(data);
      setFullStudent(data.student);
      setReloadTick((t) => t + 1);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  // V1.25.4 · Al cambiar de alumno, LIMPIAR estado antes de recargar para
  // evitar que los hijos consuman datos del alumno previo.
  useEffect(() => {
    setSummary(null);
    setFullStudent(null);
    setTab(initialTab);
    if (studentId) load();
  }, [studentId, load, initialTab]);

  const docsStatus = useMemo(
    () => (fullStudent ? computeDocsStatus(fullStudent) : null),
    [fullStudent]
  );

  const openKaiWhatsApp = useCallback(async () => {
    if (!fullStudent?.phone) {
      toast.error("Este alumno no tiene teléfono registrado");
      return;
    }
    const phone = normalizePhoneES(fullStudent.phone);
    if (!phone) {
      toast.error("El teléfono del alumno no es válido");
      return;
    }
    // Try to load the customizable template from settings (falls back to
    // the built-in default). Non-blocking — if it fails we still open WA.
    let tpl = KAI_DEFAULT_TEMPLATE;
    try {
      const { data } = await api.get("/settings/kai_wa_template");
      if (data?.value?.trim()) tpl = data.value.trim();
    } catch (_) { /* fallback default */ }
    const url = `${BACKEND_URL}/kai/cuestionario?studentId=${encodeURIComponent(fullStudent.id)}`;
    const nombre = (fullStudent.name || "").split(" ")[0] || fullStudent.name || "";
    const msg = renderTpl(tpl, { nombre, url });
    const wa = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  }, [fullStudent]);

  if (!student) return null;

  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="student-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {fullStudent?.codigo_expediente && (
              <span
                className="inline-flex items-center rounded-md bg-neutral-900 text-white px-2 py-0.5 text-xs font-bold font-mono tracking-wider"
                data-testid="student-codigo-expediente"
                title="Código de expediente Natur"
              >
                {fullStudent.codigo_expediente}
              </span>
            )}
            {student.name}
            {/* V1.37.13 · Retirados los badges "Documentación pendiente/completa"
                y el banner DocsAlertBanner por baja utilidad. Toda la
                información de archivos vive ahora en la pestaña Cloud. */}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            {/* V1.25.4 · Los botones de acción quedan deshabilitados hasta
                que fullStudent esté sincronizado con el student_id vigente
                para impedir cualquier acción sobre el alumno equivocado. */}
            {(() => {
              const synced = !!fullStudent && fullStudent.id === studentId;
              return (
                <>
                  <button
                    type="button"
                    onClick={() => setShowJustificante(true)}
                    disabled={!synced}
                    data-testid="profile-action-justificante"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileSignature className="h-3.5 w-3.5" />
                    Generar justificante de ausencia
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAltaLink(true)}
                    disabled={!synced}
                    data-testid="profile-action-alta"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Solicitar ficha digital
                  </button>
                  {/* V1.37.14 · Botón OneDrive retirado del header — la
                      funcionalidad vive ahora en la pestaña Cloud.
                      Botón "Enviar WhatsApp" retirado del header —
                      migra a la pestaña KAI. */}
                  <button
                    type="button"
                    onClick={() => setShowDgt(true)}
                    disabled={!synced}
                    data-testid="profile-action-dgt"
                    title="Documentación DGT (Mod.04 / Mod.07 / Mod.12)"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileSignature className="h-3.5 w-3.5" />
                    DGT · Documentos
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/students/${fullStudent.id}/ficha-practica/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        window.open(url, "_blank", "noopener,noreferrer");
                      } catch (err) {
                        toast.error("No se pudo generar la Ficha Práctica");
                      }
                    }}
                    disabled={!synced}
                    data-testid="profile-action-ficha-practica"
                    title="Ficha Práctica rellenada con los datos del alumno"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileCheck2 className="h-3.5 w-3.5" />
                    Ficha Práctica
                  </button>
                </>
              );
            })()}
          </div>
        </DialogHeader>

        {fullStudent && fullStudent.id === studentId && (
          <ContactStatusPanel student={fullStudent} onUpdated={load} />
        )}

        {/* V1.27 · FASE 1 · Botón manual de conversión a alumno para
            los contactos aún en etapa prospecto/interesado. */}
        {fullStudent && fullStudent.id === studentId
          && ["prospecto", "interesado"].includes(fullStudent.lifecycle_stage || "prospecto") && (
          <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-emerald-800">
                Este contacto está en etapa
                <strong className="ml-1">
                  {fullStudent.lifecycle_stage === "prospecto" ? "🔵 Prospecto" : "🟡 Interesado"}
                </strong>
              </span>
            </div>
            <button
              type="button"
              data-testid="convert-to-alumno-btn"
              onClick={async () => {
                try {
                  await api.patch(`/students/${fullStudent.id}/lifecycle-stage`, {
                    lifecycle_stage: "alumno_activo",
                    note: "Conversión manual",
                  });
                  toast.success("Convertido a Alumno activo");
                  load();
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail));
                }
              }}
              className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
            >
              🟢 Convertir a alumno
            </button>
          </div>
        )}

        {/* V1.37.13 · DocsAlertBanner retirado (poco valor práctico). */}

        {/* V1.80 · Aviso cuando la tasa se queda sin convocatorias */}
        {fullStudent && fullStudent.id === studentId && (
          <TasaRenewalBanner studentId={fullStudent.id} refreshKey={reloadTick} />
        )}

        {fullStudent && fullStudent.id === studentId && (
          <ReconciliationBanner studentId={fullStudent.id} onFixed={load} />
        )}

        {fullStudent && fullStudent.id === studentId && (
          <ExpedienteOneDrivePanel studentId={fullStudent.id} />
        )}

        {fullStudent && fullStudent.id === studentId && (
          <AltaDgtPanel studentId={fullStudent.id} />
        )}

        {/* V1.37.13 · StudentFilesPanel se mueve DENTRO de la pestaña Cloud
            (antes "Documentación"). La sección DocsSection y sus alertas se
            retiran por baja utilidad práctica. Ver render en la sección de tabs. */}

        <div className="flex gap-1 border-b border-neutral-200 -mx-6 px-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`profile-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={
                "px-3 py-2 text-sm font-semibold border-b-2 transition-colors " +
                (tab === t.key
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-neutral-500 hover:text-neutral-900")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading || !summary || !fullStudent || fullStudent.id !== studentId ? (
          <div className="py-10 text-center text-sm text-neutral-500" data-testid="student-summary-loading">Cargando…</div>
        ) : (
          <div className="pt-2">
            {tab === "resumen" && (
              <div className="space-y-4">
                <ResumenSection summary={summary} student={fullStudent} onReload={load} />
                <ReferralSection studentId={fullStudent?.id} studentName={fullStudent?.name} />
              </div>
            )}
            {tab === "horarios" && <StudentSchedulingTab student={fullStudent} />}
            {tab === "docs" && <StudentFilesPanel studentId={fullStudent.id} />}
            {tab === "kai" && <KaiStudentTab student={fullStudent} />}
            {tab === "ana" && <AnaStudentTab student={fullStudent} />}
            {/* V1.79 · La pestaña "Solicitudes" se integró en Nueva convocatoria
                de examen; las tasas y su historial se gestionan desde allí. */}
            {tab === "examenes_dgt" && <ExamenesDgtTab student={fullStudent} onReload={load} />}
            {tab === "invoice" && <InvoiceSection student={fullStudent} onReload={load} />}
            {tab === "contratos" && <ContractsTab student={fullStudent} />}
          </div>
        )}
      </DialogContent>
      {/* V1.25.4 · Diálogos hijos SÓLO reciben datos cuando la ficha vigente
          está totalmente sincronizada. Nunca `student` inicial (que podría ser
          la fila resumen), siempre `fullStudent` post-fetch para que DGT y
          otros generadores usen la Ficha Maestra actualizada. */}
      {fullStudent && fullStudent.id === studentId && (
        <>
          <JustificanteAusenciaDialog
            open={showJustificante}
            student={fullStudent}
            onClose={() => setShowJustificante(false)}
          />
          <AltaDigitalLinkDialog
            open={showAltaLink}
            student={fullStudent}
            onClose={() => setShowAltaLink(false)}
          />
          <OneDriveVaultDialog
            open={showOnedrive}
            student={fullStudent}
            onClose={() => { setShowOnedrive(false); load(); }}
          />
          <DgtDocumentsDialog
            open={showDgt}
            student={fullStudent}
            onClose={() => { setShowDgt(false); load(); }}
          />
        </>
      )}
    </Dialog>
  );
}
